/**
 * Upload tray — the surface that replaced the blocking `alert()`.
 *
 * A folder upload keeps running after the user navigates away from /upload, so
 * its outcome has to live somewhere that is always on screen and never steals
 * focus. A partial upload in particular is not an error dialog: it is a state
 * with a repair (retry the same file list), and the tray is where that repair
 * lives.
 */

import {
	AlertTriangle,
	CheckCircle2,
	ChevronDown,
	ChevronUp,
	Loader2,
	RotateCcw,
	X,
	XCircle,
} from "lucide-react";
import React, { useState } from "react";

import {
	canRetryJob,
	hasActiveJobForProject,
	isJobActive,
	UploadJob,
	useUploadManager,
} from "../../context/UploadManager";

/**
 * Progress as a Tailwind width class.
 *
 * The repo forbids the `style` prop, and Tailwind's JIT cannot see a class name
 * built at runtime — so the bar is quantised to twelfths and every class it can
 * ever use is written out literally here for the scanner to find.
 */
const PROGRESS_WIDTHS = [
	"w-0",
	"w-1/12",
	"w-2/12",
	"w-3/12",
	"w-4/12",
	"w-5/12",
	"w-6/12",
	"w-7/12",
	"w-8/12",
	"w-9/12",
	"w-10/12",
	"w-11/12",
	"w-full",
] as const;

const progressWidthClass = (progress: number): string => {
	const clamped = Math.min(100, Math.max(0, progress));
	return PROGRESS_WIDTHS[Math.round((clamped / 100) * 12)];
};

interface StatusPresentation {
	label: string;
	icon: React.ReactNode;
	className: string;
}

const presentStatus = (job: UploadJob): StatusPresentation => {
	switch (job.status) {
		case "queued":
			return {
				label: "Waiting",
				icon: <Loader2 className="w-3.5 h-3.5 text-text-muted" />,
				className: "text-text-muted",
			};
		case "uploading":
			return {
				label: `Uploading ${job.progress}%`,
				icon: (
					<Loader2 className="w-3.5 h-3.5 animate-spin text-[#2A9D8F]" />
				),
				className: "text-[#2A9D8F]",
			};
		case "done":
			return {
				label: `Uploaded ${job.result?.uploaded ?? 0} file${
					job.result?.uploaded === 1 ? "" : "s"
				}`,
				icon: <CheckCircle2 className="w-3.5 h-3.5 text-[#2A9D8F]" />,
				className: "text-[#2A9D8F]",
			};
		case "partial":
			return {
				label: "Stopped partway",
				icon: <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />,
				className: "text-amber-600",
			};
		case "failed":
		default:
			return {
				label: "Upload failed",
				icon: <XCircle className="w-3.5 h-3.5 text-red-600" />,
				className: "text-red-600",
			};
	}
};

interface UploadTrayRowProps {
	job: UploadJob;
	/**
	 * True when this job's project already has an upload queued or running —
	 * this job's own repair, or anything else aimed at the same deal room.
	 * Used only to explain a missing Retry; whether the button shows at all is
	 * `canRetryJob`'s call.
	 */
	repairInFlight: boolean;
	/** Whether the manager would accept a Retry for this job right now. */
	canRetry: boolean;
	onRetry: (jobId: string) => void;
	onDismiss: (jobId: string) => void;
}

/** Files shown per reason before collapsing to "+N more". */
const MAX_NAMED_FILES = 3;

/**
 * Why the failed files failed, grouped by reason: a batch the browser couldn't
 * send fails its files with one shared reason, which reads once, not four times.
 */
const FailureReasons: React.FC<{
	reasons: Array<{ filename: string; error: string }>;
}> = ({ reasons }) => {
	const byReason = new Map<string, string[]>();
	reasons.forEach(({ filename, error }) => {
		byReason.set(error, [...(byReason.get(error) ?? []), filename]);
	});
	return (
		<>
			{Array.from(byReason.entries()).map(([error, files]) => (
				<div key={error} className="space-y-0.5">
					<div className="truncate" title={files.join(", ")}>
						Failed: {files.slice(0, MAX_NAMED_FILES).join(", ")}
						{files.length > MAX_NAMED_FILES
							? ` +${files.length - MAX_NAMED_FILES} more`
							: ""}
					</div>
					<div className="text-red-600">{error}</div>
				</div>
			))}
		</>
	);
};

const UploadTrayRow: React.FC<UploadTrayRowProps> = ({
	job,
	repairInFlight,
	canRetry,
	onRetry,
	onDismiss,
}) => {
	const status = presentStatus(job);
	const active = isJobActive(job);
	const needsRepair = job.status === "partial" || job.status === "failed";
	const resolved = Boolean(job.resolvedByJobId);

	return (
		<li className="px-3 py-2 border-t border-gray-100 first:border-t-0">
			<div className="flex items-center gap-2">
				<span className="flex-shrink-0">{status.icon}</span>
				<span
					className="flex-1 min-w-0 truncate text-base font-medium text-text-base"
					title={job.projectName}
				>
					{job.projectName}
				</span>
				{!active && (
					<button
						type="button"
						aria-label={`Dismiss ${job.projectName} upload`}
						className="flex-shrink-0 rounded p-1 text-text-muted hover:bg-gray-100"
						onClick={() => onDismiss(job.id)}
					>
						<X className="w-3.5 h-3.5" />
					</button>
				)}
			</div>

			<div className={`mt-0.5 text-xs ${status.className}`}>{status.label}</div>

			{job.status === "uploading" && (
				<div
					role="progressbar"
					aria-valuenow={job.progress}
					aria-valuemin={0}
					aria-valuemax={100}
					aria-label={`${job.projectName} upload progress`}
					className="mt-1.5 h-1 w-full overflow-hidden rounded bg-gray-100"
				>
					<div
						className={`h-full rounded bg-[#2A9D8F] transition-all duration-200 ${progressWidthClass(
							job.progress
						)}`}
					/>
				</div>
			)}

			{needsRepair && job.result && (
				<div className="mt-1 space-y-0.5 text-xs text-text-muted">
					<div>
						{job.result.uploaded} uploaded · {job.result.failed.length} failed ·{" "}
						{job.result.notAttempted.length} not attempted
					</div>
					{job.result.reasons && job.result.reasons.length > 0 ? (
						<FailureReasons reasons={job.result.reasons} />
					) : (
						job.result.failed.length > 0 && (
							<div className="truncate" title={job.result.failed.join(", ")}>
								Failed: {job.result.failed.join(", ")}
							</div>
						)
					)}
					{/* The deal room de-duplicates by file content, so re-sending the
					    whole folder is the cheap, correct repair. */}
					<div>Retry re-sends the whole folder; nothing is catalogued twice.</div>
				</div>
			)}

			{canRetry && (
				<button
					type="button"
					className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-[#004C4B] px-2.5 py-1 text-sm font-medium text-white transition-colors hover:bg-[#003a3a]"
					onClick={() => onRetry(job.id)}
				>
					<RotateCcw className="w-3.5 h-3.5" />
					Retry
				</button>
			)}

			{/* Say WHY the button is gone. A failed row with no action and no
			    explanation reads as a dead end, when in fact the repair is either
			    already on its way or already finished. */}
			{needsRepair && !canRetry && (
				<div className="mt-1.5 text-xs text-text-muted">
					{resolved
						? "Repaired by a later upload"
						: repairInFlight
							? job.retriedByJobId
								? "Retried — repair in progress"
								: "Upload in progress for this deal room"
							: null}
				</div>
			)}
		</li>
	);
};

const UploadTray: React.FC = () => {
	const { jobs, retryUpload, dismissJob, clearFinishedJobs } =
		useUploadManager();
	const [isExpanded, setIsExpanded] = useState(false);

	if (jobs.length === 0) return null;

	const activeCount = jobs.filter(isJobActive).length;
	const needsAttention = jobs.some(
		(job) => job.status === "partial" || job.status === "failed"
	);

	return (
		<div className="fixed bottom-4 right-4 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-lg">
			<div className="flex items-center gap-2 px-3 py-2">
				<button
					type="button"
					aria-expanded={isExpanded}
					className="flex flex-1 items-center gap-2 text-left"
					onClick={() => setIsExpanded((prev) => !prev)}
				>
					{activeCount > 0 ? (
						<Loader2 className="w-4 h-4 animate-spin text-[#2A9D8F]" />
					) : needsAttention ? (
						<AlertTriangle className="w-4 h-4 text-amber-600" />
					) : (
						<CheckCircle2 className="w-4 h-4 text-[#2A9D8F]" />
					)}
					<span className="text-base font-medium text-text-base">
						{activeCount > 0
							? `Uploading ${activeCount} deal room${activeCount === 1 ? "" : "s"}`
							: "Uploads"}
					</span>
					{isExpanded ? (
						<ChevronDown className="ml-auto w-4 h-4 text-text-muted" />
					) : (
						<ChevronUp className="ml-auto w-4 h-4 text-text-muted" />
					)}
				</button>
				<button
					type="button"
					aria-label="Dismiss finished uploads"
					className="rounded p-1 text-text-muted hover:bg-gray-100"
					onClick={clearFinishedJobs}
				>
					<X className="w-4 h-4" />
				</button>
			</div>

			{isExpanded && (
				<ul className="max-h-72 overflow-y-auto border-t border-gray-100">
					{jobs.map((job) => (
						<UploadTrayRow
							key={job.id}
							job={job}
							repairInFlight={hasActiveJobForProject(jobs, job.projectId)}
							canRetry={canRetryJob(jobs, job)}
							onRetry={retryUpload}
							onDismiss={dismissJob}
						/>
					))}
				</ul>
			)}
		</div>
	);
};

export default UploadTray;
